import snakeCase from 'lodash/snakeCase';
import mapKeys from 'lodash/mapKeys';
import uuid from 'uuid/v4';
import getSplashApiClient from 'lib/SplashApiClient/singleton';
import get from 'lodash/get';

/**
 * Wraps a SplashApiClient with the form builder API actions.
 */
export default class FormBuilderApiClient {
    /**
     * @param {SplashApiClient} apiClient
     */
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * @return {Promise}
     */
    loadBootstrapData() {
        return this.apiClient.get('/form-builder/bootstrap');
    }

    /**
     * @param {string} id
     * @return {Promise}
     */
    loadFormDataByFormId(id) {
        return this.apiClient.get(`/form-builder/forms/${id}`);
    }

    /**
     * @param {string} id
     * @param {string} version
     * @return {Promise}
     */
    loadFormDataByFormIdAndVersion(formId, versionId) {
        return this.apiClient.get(`/form-builder/forms/${formId}/versions/${versionId}`);
    }

    /**
     * @param {string} id
     * @return {Promise}
     */
    loadEventInfoByEventId(id, page) {
        return this.apiClient.get(`/forms-history/event/${id}`, { page });
    }

    /**
     * @param {string} eventId
     * @return {Promise}
     */
    loadFormOwnedByEvent(eventId) {
        return this.apiClient.get(`/form-builder/owner/event/${eventId}`);
    }

    /**
     * @param {string} eventId
     * @param {string} rsvpMethod
     * @return {Promise}
     */
    loadFormOwnedByEventAndByRsvpMethod(eventId, rsvpMethod) {
        return this.apiClient.get(`/form-builder/owner/event/${eventId}`, {
            form_discriminator: rsvpMethod,
        });
    }

    /**
     * @param {string} ownerType
     * @param {string} ownerId
     * @return {Promise}
     */
    loadFormByOwnerAndOwnerId(ownerType, ownerId, discriminator) {
        return this.apiClient.get(
            `/form-builder/owner/${ownerType}/${ownerId}`,
            discriminator && { form_discriminator: discriminator },
        );
    }

    /**
     * @param {string} ownerType
     * @param {string} ownerId
     * @return {Promise}
     */
    createFormByOwnerAndOwnerId(ownerType, ownerId) {
        return this.apiClient.post(`/form-builder/owner/${ownerType}/${ownerId}`);
    }

    /**
     * @param {string} ticketId
     * @param {string} formId
     * @return {Promise}
     */
    createFormOwnershipWithTicketType(ticketId, formId) {
        return this.apiClient.post(`/form-builder/owner/ticket-type/${ticketId}`, {
            form_id: formId,
        });
    }

    /**
     * @return {Promise}
     */
    fetchTicketSaleForm() {
        return this.apiClient.get('/form-builder/forms/default/ticket-type');
    }

    /**
     * @param {object} data
     * @return {Promise}
     */
    saveNewFormData(data) {
        return this.apiClient.post('/form-builder/forms', data);
    }

    /**
     * @param {object} data
     * @return {Promise}
     */
    updateFormData(data) {
        return this.apiClient.put(`/form-builder/forms/${get(data, 'form.id', '')}`, data);
    }

    /**
     * @param {object} question
     * @return {Promise}
     */
    addQuestionToFormTemplateLibrary(question) {
        const nodeData = {
            ...mapKeys(question, (value, key) => snakeCase(key)),
            id: `_${uuid()}`,
        };

        const nodeTypesToTemplateTypes = {
            question: 'FormTemplateQuestion',
            group: 'FormTemplateGroup',
        };

        return this.apiClient.post('/form-builder/library', {
            data: {
                type: nodeTypesToTemplateTypes[question.type],
                data: nodeData,
            },
        });
    }

    updateFormOwnership(ownerType, eventId, newFormId) {
        return this.apiClient.put(`/form-builder/owner/${ownerType}/${eventId}`, {
            form_id: newFormId,
        });
    }
}

export const getFormBuilderApiClient = () => new FormBuilderApiClient(getSplashApiClient());
